import { useEffect, useRef } from 'react';

export function useGlassHover<T extends HTMLElement>(): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (matchMedia('(pointer: coarse)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rect = el.getBoundingClientRect();
    let pending = false;
    let lastEvent: PointerEvent | null = null;

    const apply = () => {
      pending = false;
      const e = lastEvent;
      if (!e) return;
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      el.style.setProperty('--mx', `${px * 100}%`);
      el.style.setProperty('--my', `${py * 100}%`);
      el.style.setProperty('--rx', `${((px - 0.5) * 2 * 2.5).toFixed(2)}deg`);
      el.style.setProperty('--ry', `${((0.5 - py) * 2 * 2.5).toFixed(2)}deg`);
      el.style.setProperty('--spec', '1');
    };

    const onEnter = () => {
      rect = el.getBoundingClientRect();
    };
    const onResize = () => {
      rect = el.getBoundingClientRect();
    };
    const onMove = (e: PointerEvent) => {
      lastEvent = e;
      if (pending) return;
      pending = true;
      requestAnimationFrame(apply);
    };
    const onLeave = () => {
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
      el.style.setProperty('--spec', '0');
    };

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    window.addEventListener('resize', onResize);

    return () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return ref;
}
